import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { CoreConfigService } from '@core/services/config.service';
import { LoginService } from 'app/services/auth/login.service';
import { EnterpriseBrandingService } from 'app/services/enterprise-branding.service';
import { AuthenticationService } from 'app/auth/service';
import {
  LogicielCaisseAuthService,
  LogicielCaisseBootstrapStatus,
} from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { environment } from 'environments/environment';
import { AuthSyncService } from 'app/services/auth/auth-sync.service';
import { withCrossFrontAuth } from 'app/services/logiciel-caisse/logiciel-caisse-return-url.util';

@Component({
  selector: 'app-auth-login-v2',
  templateUrl: './auth-login-v2.component.html',
  styleUrls: ['./auth-login-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AuthLoginV2Component implements OnInit, OnDestroy {
  public coreConfig: any;
  public loginForm: UntypedFormGroup;
  public loading = false;
  public submitted = false;
  public returnUrl: string;
  public error = '';
  /** Message visible si ERP OK mais logiciel caisse non connecté. */
  public lcWarning = '';
  public passwordTextType: boolean;
  private _unsubscribeAll: Subject<void>;

  constructor(
    private loginService: LoginService,
    private _coreConfigService: CoreConfigService,
    private _formBuilder: UntypedFormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private _enterpriseBranding: EnterpriseBrandingService,
    private _authenticationService: AuthenticationService,
    private _logicielCaisseAuth: LogicielCaisseAuthService,
    private _authSync: AuthSyncService
  ) {
    this._unsubscribeAll = new Subject();
    this._coreConfigService.config = {
      layout: {
        navbar: { hidden: true },
        menu: { hidden: true },
        footer: { hidden: true },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }

  get f() {
    return this.loginForm.controls;
  }

  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  onSubmit() {
    this.submitted = true;
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.lcWarning = '';
    const email = String(this.loginForm.value.email || '').trim();
    const password = String(this.loginForm.value.password || '');

    console.group('[Login Hub] Connexion ERP + logiciel caisse');
    console.log('Soumission', { email, passwordLength: password.length });

    this.loginService.login({ email, password }).subscribe({
      next: (data) => {
        const token = data?._token || data?.token;
        console.log('[Login Hub] Réponse ERP', {
          hasUser: !!data?.user,
          hasToken: !!token,
          userId: data?.user?.id,
          message: data?.message || data?.error,
        });

        if (!(data?.user && token)) {
          this.loading = false;
          this.error =
            data?.message || data?.error || 'Email ou mot de passe incorrect';
          console.error('[Login Hub] ÉCHEC ERP', this.error, data);
          console.groupEnd();
          return;
        }

        this._authenticationService.applyLoginSession(data);
        this._enterpriseBranding.syncFromUser(data.user);
        console.log('[Login Hub] ERP OK — session appliquée', {
          erpUserId: data.user?.id,
          ctx: this._logicielCaisseAuth.getErpContext(),
        });

        this._logicielCaisseAuth
          .authenticateAfterErpLogin(email, password)
          .pipe(finalize(() => (this.loading = false)))
          .subscribe({
            next: (lcStatus) => this.afterErpAndLcAuth(lcStatus),
            error: (e) => {
              console.error('[Login Hub] Exception auth LC', e);
              this.lcWarning =
                'ERP connecté, mais erreur lors de l’authentification logiciel caisse. Voir la console (F12).';
              console.groupEnd();
              void this._router.navigate(['/tiers/acces-logiciel-caisse/onboarding'], {
                queryParams: { returnUrl: this.returnUrl || '/home' },
              });
            },
          });
      },
      error: (e) => {
        this.loading = false;
        this.error = 'Email ou mot de passe incorrect';
        console.error('[Login Hub] ÉCHEC HTTP ERP', e);
        console.groupEnd();
      },
    });
  }

  /**
   * Après ERP + tentative LC :
   * - ready → home / returnUrl (session LC propagée via auth=)
   * - needs_password → écran « Connexion logiciel caisse »
   * - needs_setup → onboarding « Créer un accès »
   */
  private afterErpAndLcAuth(lcStatus: LogicielCaisseBootstrapStatus): void {
    const returnUrl = String(this.returnUrl || '').trim();
    const safeReturn = returnUrl && returnUrl !== '/' ? returnUrl : '/home';
    const lcOk = lcStatus === 'ready' && this._logicielCaisseAuth.isAuthenticated();
    const ctx = this._logicielCaisseAuth.getErpContext();

    const diagnostic = {
      lcStatus,
      lcOk,
      isAuthenticated: this._logicielCaisseAuth.isAuthenticated(),
      erpContext: ctx,
      lcSession: this._logicielCaisseAuth.getSession(),
      hasLcToken: !!this._logicielCaisseAuth.getLogicielCaisseToken(),
      returnUrl: safeReturn,
    };

    if (lcOk) {
      console.log('[Login Hub] ERP + logiciel caisse OK', diagnostic);
      console.groupEnd();
      if (returnUrl && /^https?:\/\//i.test(returnUrl)) {
        window.location.href = withCrossFrontAuth(returnUrl, this._authSync.buildAuthQueryParams());
        return;
      }
      if (returnUrl && returnUrl.startsWith('/') && returnUrl !== '/') {
        void this._router.navigateByUrl(returnUrl);
        return;
      }
      void this._router.navigate(['/home']);
      return;
    }

    // ERP OK mais LC non authentifié — afficher + logger le problème.
    const human =
      lcStatus === 'needs_password'
        ? 'ERP connecté, mais le logiciel caisse demande le mot de passe caisse (accès déjà existant).'
        : 'ERP connecté, mais aucun accès logiciel caisse valide — création / configuration requise.';

    this.lcWarning = `${human} Détails dans la console (F12 → onglet Console, filtre « Login LC »).`;
    console.warn('[Login Hub] PROBLÈME AUTH LOGICIEL CAISSE', human);
    console.warn('[Login Hub] Diagnostic complet', diagnostic);
    console.warn('[Login Hub] Redirection setup', {
      path:
        lcStatus === 'needs_password'
          ? '/tiers/acces-logiciel-caisse/add'
          : '/tiers/acces-logiciel-caisse/onboarding',
      returnUrl: safeReturn,
    });
    console.groupEnd();

    const setupPath =
      lcStatus === 'needs_password'
        ? '/tiers/acces-logiciel-caisse/add'
        : '/tiers/acces-logiciel-caisse/onboarding';

    // Laisse le temps de lire l’alerte avant redirection.
    window.setTimeout(() => {
      void this._router.navigate([setupPath], {
        queryParams: { returnUrl: safeReturn },
      });
    }, 1200);
  }

  ngOnInit() {
    if ((environment as { currentModule?: string }).currentModule !== 'hub') {
      const hub = ((environment as { hubUrl?: string }).hubUrl || 'https://front-hub.agrosilo-solutions.com').replace(/\/$/, '');
      const returnUrl = this._route.snapshot.queryParams['returnUrl'];
      const q = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
      window.location.href = `${hub}/login${q}`;
      return;
    }

    this.loginForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
    this.returnUrl = this._route.snapshot.queryParams['returnUrl'] || '/';
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
      });
  }

  ngOnDestroy() {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
